import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  CircleStackIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  XCircleIcon,
  CheckCircleIcon,
  UsersIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { SearchableUserSelect } from '@/components/ui/SearchableUserSelect'
import {
  getGrades,
  createGrade,
  updateGrade,
  deleteGrade,
  createStream,
  type Grade as ApiGrade,
  type CreateGradeRequest,
  type CreateStreamRequest
} from '@/api/academicApi'
import toast from 'react-hot-toast'

// Form schema for creating new grade
const addGradeSchema = z.object({
  name: z.string().min(1, 'Grade name is required'),
  description: z.string().optional(),
  isActive: z.boolean().default(true),
})

type AddGradeData = z.infer<typeof addGradeSchema>

// Use ApiGrade type from academicApi
type Grade = ApiGrade

const GradesPage = () => {
  const navigate = useNavigate()
  const [grades, setGrades] = useState<Grade[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [levelFilter, setLevelFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showAddStreamModal, setShowAddStreamModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingGrade, setEditingGrade] = useState<Grade | null>(null)
  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null)
  const [selectedGradeForStream, setSelectedGradeForStream] = useState<Grade | null>(null)
  const [expandedGrades, setExpandedGrades] = useState<Set<string>>(new Set())

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  
  // Stats state
  const [stats, setStats] = useState({ 
    total: 0, 
    active: 0, 
    inactive: 0,
    totalStreams: 0,
    totalStudents: 0
  })

  const addGradeForm = useForm<AddGradeData>({
    resolver: zodResolver(addGradeSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
    }
  })

  // Edit form for updating grades
  const editGradeForm = useForm<AddGradeData>({
    resolver: zodResolver(addGradeSchema),
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
    }
  })

  // Stream form for adding streams to grades
  const addStreamForm = useForm({
    defaultValues: {
      name: '',
      classTeacher: '',
      description: '',
    }
  })

  // Debounced search query to prevent excessive API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // Debounce the search query
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
      // Reset to first page when search changes
      if (searchQuery !== debouncedSearchQuery) {
        setCurrentPage(1)
      }
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  // Fetch grades when debounced search, page, or status changes
  useEffect(() => {
    fetchGrades()
  }, [currentPage, debouncedSearchQuery, statusFilter])

  const toggleGradeExpansion = (gradeId: string) => {
    setExpandedGrades(prev => {
      const newSet = new Set(prev)
      if (newSet.has(gradeId)) {
        newSet.delete(gradeId)
      } else {
        newSet.add(gradeId)
      }
      return newSet
    })
  }

  const handleAddStreamToGrade = (grade: Grade) => {
    setSelectedGradeForStream(grade)
    addStreamForm.setValue('name', generateStreamName(grade))
    setShowAddStreamModal(true)
  }

  const generateStreamName = (grade: Grade): string => {
    const existingStreamsCount = grade.streams?.length || 0
    const nextLetter = String.fromCharCode(65 + existingStreamsCount) // A=65, B=66, etc.
    return `${grade.name}${nextLetter}`
  }

  const handleCreateStream = async (streamData: any) => {
    if (!selectedGradeForStream) return
    
    try {
      const createData: CreateStreamRequest = {
        name: streamData.name,
        gradeId: selectedGradeForStream.id,
        classTeacher: streamData.classTeacher,
        description: streamData.description
      }
      
      await createStream(createData)
      toast.success(`Stream "${streamData.name}" added to ${selectedGradeForStream.name}`)
      setShowAddStreamModal(false)
      addStreamForm.reset()
      setSelectedGradeForStream(null)
      fetchGrades()
    } catch (error: any) {
      console.error('Failed to create stream:', error)
      const message = error?.response?.data?.message || 'Failed to create stream'
      toast.error(message)
    }
  }

  const fetchGrades = async () => {
    // Only show full loading state on initial load, not on search/filter changes
    if (grades.length === 0) {
      setLoading(true)
    }
    try {
      const response = await getGrades({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchQuery.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter as 'active' | 'inactive' : 'all',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      })

      setGrades(response.grades)
      setCurrentPage(response.pagination.currentPage)

      // Calculate stats from response data
      setStats({
        total: response.pagination.totalGrades,
        active: response.grades.filter(g => g.isActive).length,
        inactive: response.grades.filter(g => !g.isActive).length,
        totalStreams: response.grades.reduce((sum, g) => sum + (g.streamCount || 0), 0),
        totalStudents: response.grades.reduce((sum, g) => sum + (g.studentCount || 0), 0),
      })
      
    } catch (error) {
      console.error('Failed to fetch grades:', error)
      toast.error('Failed to load grades')
    } finally {
      setLoading(false)
    }
  }

  const handleAddGrade = async (gradeData: AddGradeData) => {
    try {
      const createData: CreateGradeRequest = {
        name: gradeData.name,
        description: gradeData.description
      }
      
      await createGrade(createData)
      toast.success(`Grade "${gradeData.name}" created successfully!`)
      setShowAddModal(false)
      addGradeForm.reset()
      fetchGrades()
      
    } catch (error: any) {
      console.error('Failed to create grade:', error)
      const message = error?.response?.data?.message || 'Failed to create grade'
      toast.error(message)
    }
  }

  const handleEditGrade = (grade: Grade) => {
    setEditingGrade(grade)
    editGradeForm.reset({
      name: grade.name,
      description: grade.description || '',
      isActive: grade.isActive,
    })
    setShowEditModal(true)
  }

  const handleSaveEdit = async (data: AddGradeData) => {
    if (!editingGrade) return

    try {
      await updateGrade(editingGrade.id, {
        name: data.name,
        description: data.description,
        isActive: data.isActive,
      })
      toast.success(`"${data.name}" updated successfully!`)
      setShowEditModal(false)
      setEditingGrade(null)
      editGradeForm.reset()
      fetchGrades()
    } catch (error: any) {
      console.error('Failed to update grade:', error)
      const message = error?.response?.data?.message || 'Failed to update grade'
      toast.error(message)
    }
  }

  const handleDeleteGrade = async (grade: Grade) => {
    if (grade.streamCount > 0) {
      toast.error(`Cannot delete "${grade.name}". It has ${grade.streamCount} streams. Please delete streams first.`)
      return
    }

    if (confirm(`Are you sure you want to delete "${grade.name}"?`)) {
      try {
        await deleteGrade(grade.id)
        toast.success(`"${grade.name}" deleted successfully!`)
        fetchGrades()
      } catch (error: any) {
        console.error('Failed to delete grade:', error)
        const message = error?.response?.data?.message || 'Failed to delete grade'
        toast.error(message)
      }
    }
  }

  const handleToggleStatus = async (grade: Grade) => {
    const newStatus = !grade.isActive
    const statusText = newStatus ? 'activated' : 'deactivated'

    try {
      await updateGrade(grade.id, {
        isActive: newStatus,
      })
      toast.success(`"${grade.name}" ${statusText} successfully!`)
      fetchGrades()
    } catch (error: any) {
      console.error('Failed to toggle grade status:', error)
      const message = error?.response?.data?.message || 'Failed to update grade status'
      toast.error(message)
    }
  }

  // Navigate to students page filtered by stream
  const handleViewStudentsInStream = (stream: { id: string; name: string }, grade: Grade) => {
    navigate(`/schoolAdmin/students?streamId=${stream.id}&streamName=${encodeURIComponent(stream.name)}&gradeName=${encodeURIComponent(grade.name)}`)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="dashboard-stat-card animate-pulse">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="dashboard-card animate-pulse">
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CircleStackIcon className="h-8 w-8 text-blue-600 mr-3" />
            Grades Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage academic grade levels and their configurations
          </p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Grade
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Grades</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <CircleStackIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Grades</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Streams</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalStreams}</p>
            </div>
            <Squares2X2Icon className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Students</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
            </div>
            <UsersIcon className="h-8 w-8 text-indigo-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Grades</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        <form className="flex-1 relative max-w-md" onSubmit={(e) => e.preventDefault()}>
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search grades..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            className="form-input pl-10"
          />
        </form>
        
        <div className="flex items-center space-x-3">
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Levels</option>
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>Level {i + 1}</option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Grades Expandable List */}
      <div className="space-y-4">
        {grades.map((grade) => (
          <motion.div
            key={grade.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="dashboard-card"
          >
            {/* Grade Header */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleGradeExpansion(grade.id)}
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{grade.name.slice(-1)}</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{grade.name}</h3>
                  <p className="text-sm text-gray-600">
                    {grade.streamCount} streams • {grade.studentCount} students
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  grade.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {grade.isActive ? 'Active' : 'Inactive'}
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleAddStreamToGrade(grade)
                    }}
                    className="btn btn-ghost text-xs text-blue-600"
                    title="Add Stream"
                  >
                    <PlusIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEditGrade(grade)
                    }}
                    className="btn btn-ghost text-xs"
                    title="Edit Grade"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleToggleStatus(grade)
                    }}
                    className={`btn btn-ghost text-xs ${grade.isActive ? 'text-orange-600' : 'text-green-600'}`}
                    title={grade.isActive ? 'Deactivate Grade' : 'Activate Grade'}
                  >
                    {grade.isActive ? (
                      <XCircleIcon className="h-4 w-4" />
                    ) : (
                      <CheckCircleIcon className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteGrade(grade)
                    }}
                    className="btn btn-ghost text-xs text-red-600"
                    title="Delete Grade"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                    </svg>
                  </button>
                </div>
                {expandedGrades.has(grade.id) ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>
            </div>

            {/* Streams List (Expandable) */}
            <AnimatePresence>
              {expandedGrades.has(grade.id) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-t border-gray-200"
                >
                  <div className="p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900 flex items-center">
                        <Squares2X2Icon className="h-4 w-4 text-purple-600 mr-2" />
                        Streams in {grade.name}
                      </h4>
                      <button
                        onClick={() => handleAddStreamToGrade(grade)}
                        className="btn btn-primary btn-sm"
                      >
                        <PlusIcon className="h-3 w-3 mr-1" />
                        Add Stream
                      </button>
                    </div>

                    {grade.streams && grade.streams.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {grade.streams.map((stream) => (
                          <div key={stream.id} className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                                  <span className="text-white font-bold text-sm">{stream.name.slice(-1)}</span>
                                </div>
                                <div>
                                  <h5 className="font-semibold text-gray-900">{stream.name}</h5>
                                  <p className="text-xs text-gray-600">
                                    {stream.classTeacher || 'No teacher assigned'}
                                  </p>
                                </div>
                              </div>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                stream.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {stream.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>

                            {/* Stream Details */}
                            {stream.description && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-600">{stream.description}</p>
                              </div>
                            )}

                            {/* Stream Actions */}
                            <div className="flex justify-end space-x-1 mt-3">
                              <button
                                onClick={() => handleViewStudentsInStream(stream, grade)}
                                className="btn btn-ghost text-xs"
                                title="View Students"
                              >
                                <UsersIcon className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => toast.success(`Edit ${stream.name}`)}
                                className="btn btn-ghost text-xs"
                                title="Edit Stream"
                              >
                                <PencilIcon className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Squares2X2Icon className="mx-auto h-8 w-8 text-gray-400" />
                        <p className="text-sm text-gray-500 mt-2">No streams created for this grade</p>
                        <button
                          onClick={() => handleAddStreamToGrade(grade)}
                          className="mt-3 btn btn-primary btn-sm"
                        >
                          <PlusIcon className="h-3 w-3 mr-1" />
                          Create First Stream
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {grades.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <CircleStackIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No grades found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || levelFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by creating your first grade level'
              }
            </p>
            <button 
              onClick={() => setShowAddModal(true)}
              className="mt-4 btn btn-primary"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create First Grade
            </button>
          </div>
        </motion.div>
      )}

      {/* Add Grade Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Add New Grade</h3>
                    <p className="text-sm text-gray-500">Create a new academic grade level</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addGradeForm.handleSubmit(handleAddGrade)} className="p-6 space-y-4" autoComplete="off">
                {/* Hidden fields to prevent autofill */}
                <input type="text" style={{display:'none'}} autoComplete="off" />
                <input type="email" style={{display:'none'}} autoComplete="off" />
                
                <div className="space-y-4">
                  <Input
                    {...addGradeForm.register('name')}
                    label="Grade Name"
                    placeholder="e.g., Grade 1, Form 4, Pre-K"
                    autoComplete="off"
                    data-lpignore="true"
                    error={addGradeForm.formState.errors.name?.message}
                  />

                  <Input
                    {...addGradeForm.register('description')}
                    label="Description (Optional)"
                    placeholder="Brief description of this grade level"
                    autoComplete="off"
                    data-lpignore="true"
                    error={addGradeForm.formState.errors.description?.message}
                  />

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...addGradeForm.register('isActive')}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Grade is active
                    </label>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">After Creating Grade</h5>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>• Create streams within this grade (Grade 5A, 5B, 5C)</p>
                        <p>• Assign students to appropriate streams</p>
                        <p>• Create lessons that can be taught across streams</p>
                        <p>• Set up timetables and class schedules</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <CircleStackIcon className="h-4 w-4 mr-2" />
                    Create Grade
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grade Details Modal */}
      <AnimatePresence>
        {selectedGrade && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedGrade(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-xl">{selectedGrade.name.slice(-1)}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedGrade.name}</h3>
                      <p className="text-sm text-gray-500">{selectedGrade.streamCount} streams • {selectedGrade.studentCount || 0} students</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedGrade(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Grade Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Grade Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Grade Name</span>
                        <span className="text-sm text-gray-900 font-medium">{selectedGrade.name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Description</span>
                        <span className="text-sm text-gray-900 font-medium">
                          {selectedGrade.description || 'No description'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Created</span>
                        <span className="text-sm text-gray-900 font-medium">{new Date(selectedGrade.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          selectedGrade.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {selectedGrade.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Statistics */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Statistics</h4>
                    <div className="space-y-4">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <Squares2X2Icon className="h-6 w-6 text-purple-600" />
                          <div>
                            <p className="text-lg font-bold text-purple-900">{selectedGrade.streamCount}</p>
                            <p className="text-sm text-purple-700">Streams in this grade</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                        <div className="flex items-center space-x-3">
                          <UsersIcon className="h-6 w-6 text-indigo-600" />
                          <div>
                            <p className="text-lg font-bold text-indigo-900">{selectedGrade.studentCount}</p>
                            <p className="text-sm text-indigo-700">Students enrolled</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedGrade.description && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selectedGrade.description}</p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex justify-between">
                  <Button
                    onClick={() => {
                      setSelectedGrade(null)
                      // Navigate to streams for this grade
                      toast.success(`Navigate to streams for ${selectedGrade.name}`)
                    }}
                    variant="secondary"
                  >
                    <Squares2X2Icon className="h-4 w-4 mr-2" />
                    Manage Streams
                  </Button>
                  <Button
                    onClick={() => handleEditGrade(selectedGrade)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Grade
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Stream Modal (for specific grade) */}
      <AnimatePresence>
        {showAddStreamModal && selectedGradeForStream && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddStreamModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Add Stream to {selectedGradeForStream.name}
                    </h3>
                    <p className="text-sm text-gray-500">Create a new stream within this grade</p>
                  </div>
                  <button
                    onClick={() => setShowAddStreamModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addStreamForm.handleSubmit(handleCreateStream)} className="p-6 space-y-4" autoComplete="off">
                {/* Hidden fields to prevent autofill */}
                <input type="text" style={{display:'none'}} autoComplete="off" />
                <input type="email" style={{display:'none'}} autoComplete="off" />
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center space-x-3">
                    <CircleStackIcon className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Parent Grade: {selectedGradeForStream.name}</p>
                      <p className="text-sm text-blue-700">{selectedGradeForStream.streamCount} streams • {selectedGradeForStream.studentCount || 0} students</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Input
                    {...addStreamForm.register('name')}
                    label="Stream Name"
                    placeholder={generateStreamName(selectedGradeForStream)}
                    autoComplete="off"
                    data-lpignore="true"
                  />

                  <SearchableUserSelect
                    label="Class Teacher (Optional)"
                    placeholder="Search for class teacher..."
                    value={addStreamForm.watch('classTeacher')}
                    onChange={(_teacherId, teacher) => {
                      addStreamForm.setValue('classTeacher', teacher ? teacher.fullName : '')
                    }}
                    roleFilter="teacher"
                  />

                  <Input
                    {...addStreamForm.register('description')}
                    label="Description (Optional)"
                    placeholder="e.g., Mathematics focused stream"
                    autoComplete="off"
                    data-lpignore="true"
                  />
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-purple-900">Stream Creation</h5>
                      <div className="text-sm text-purple-700 mt-1 space-y-1">
                        <p>• Stream will be created within {selectedGradeForStream.name}</p>
                        <p>• Students can be assigned to this stream after creation</p>
                        <p>• Lessons can be assigned to this stream</p>
                        <p>• Class teacher will manage this stream</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddStreamModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Squares2X2Icon className="h-4 w-4 mr-2" />
                    Create Stream
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Grade Modal */}
      <AnimatePresence>
        {showEditModal && editingGrade && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowEditModal(false)
              setEditingGrade(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Grade</h3>
                    <p className="text-sm text-gray-500">Update grade information</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingGrade(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={editGradeForm.handleSubmit(handleSaveEdit)} className="p-6 space-y-4" autoComplete="off">
                <input type="text" style={{display:'none'}} autoComplete="off" />

                <div className="space-y-4">
                  <Input
                    {...editGradeForm.register('name')}
                    label="Grade Name"
                    placeholder="e.g., Grade 1, Form 4, Pre-K"
                    autoComplete="off"
                    data-lpignore="true"
                    error={editGradeForm.formState.errors.name?.message}
                  />

                  <Input
                    {...editGradeForm.register('description')}
                    label="Description (Optional)"
                    placeholder="Brief description of this grade level"
                    autoComplete="off"
                    data-lpignore="true"
                    error={editGradeForm.formState.errors.description?.message}
                  />

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...editGradeForm.register('isActive')}
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Grade is active
                    </label>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-blue-900">Grade Information</h5>
                      <div className="text-sm text-blue-700 mt-1 space-y-1">
                        <p>• Streams: {editingGrade.streamCount}</p>
                        <p>• Students: {editingGrade.studentCount || 0}</p>
                        <p>• Deactivating a grade will hide it from new enrollments</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingGrade(null)
                    }}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default GradesPage